import { pipe, filter, map, sortBy } from 'remeda';
import type { ExposureEvent } from '@types';

export type HrEvent = { timestamp: number; hr: number };

export type ExposureItemStat = {
  itemIds: number[];
  score: number;
  viewTimeMs: number;
  hrAtFeedback: number | null;
};

function normalizeItemIdsForKey(itemIds: number[]): string {
  return [...itemIds].sort((a, b) => a - b).join('|');
}

function getItemIdsFromEvent(
  event:
    | { type: 'itemShown'; itemIds?: number[] }
    | { type: 'itemFeedback'; itemIds?: number[] }
): number[] {
  const ids = event.itemIds;
  if (Array.isArray(ids) && ids.length > 0) return ids;
  return [];
}

/**
 * Returns per-food statistics for exposure: pairs each itemFeedback with the
 * previous matching itemShown and HR at feedback time.
 */
export function getExposureItemStats(
  exposureEvents: ExposureEvent[],
  hrEvents: HrEvent[]
): ExposureItemStat[] {
  const shows = pipe(
    exposureEvents,
    filter(
      (e): e is Extract<ExposureEvent, { type: 'itemShown' }> =>
        e.type === 'itemShown'
    ),
    map(e => ({ timestamp: e.timestamp, itemIds: getItemIdsFromEvent(e) }))
  );
  const feedbacks = pipe(
    exposureEvents,
    filter(
      (e): e is Extract<ExposureEvent, { type: 'itemFeedback' }> =>
        e.type === 'itemFeedback'
    ),
    map(e => ({
      timestamp: e.timestamp,
      itemIds: getItemIdsFromEvent(e),
      score: e.score,
    }))
  );

  const sortedHr = sortBy(hrEvents, e => e.timestamp);

  function hrAtFeedback(feedbackTimestamp: number): number | null {
    if (sortedHr.length === 0) return null;
    const firstAfterIndex = sortedHr.findIndex(
      h => h.timestamp >= feedbackTimestamp
    );
    if (firstAfterIndex >= 0) return sortedHr[firstAfterIndex].hr;
    return sortedHr[sortedHr.length - 1].hr;
  }

  return pipe(
    feedbacks,
    map(fb => {
      const fbKey = normalizeItemIdsForKey(fb.itemIds);
      const matchingShow = pipe(
        shows,
        filter(
          s =>
            s.timestamp < fb.timestamp &&
            normalizeItemIdsForKey(s.itemIds) === fbKey
        ),
        sortBy(s => s.timestamp),
        arr => arr[arr.length - 1]
      );
      const viewTimeMs = matchingShow
        ? fb.timestamp - matchingShow.timestamp
        : 0;
      return {
        itemIds: fb.itemIds,
        score: fb.score,
        viewTimeMs,
        hrAtFeedback: hrAtFeedback(fb.timestamp),
      };
    })
  );
}
